import { Board, Card } from './api';

export const CARD_EVENT_NAMES = {
  created: '.card.created',
  updated: '.card.updated',
  moved: '.card.moved',
  deleted: '.card.deleted',
} as const;

export interface CardCreatedEvent {
  card: Card;
  board_id: number;
}

export interface CardUpdatedEvent {
  card: Card;
  board_id: number;
}

export interface CardMovedEvent {
  card: Card;
  from_column_id: number;
  board_id: number;
}

export interface CardDeletedEvent {
  card_id: number;
  column_id: number;
  board_id: number;
}

const sortByPosition = (cards: Card[]): Card[] => [...cards].sort((a, b) => a.position - b.position);

const mapColumn = (board: Board, columnId: number, updater: (cards: Card[]) => Card[]): Board => ({
  ...board,
  columns: board.columns.map((column) =>
    column.id === columnId ? { ...column, cards: updater(column.cards) } : column
  ),
});

// Recherche par id dans toutes les colonnes : le column_id d'un event peut
// être périmé si un event concurrent sur la même carte l'a déjà traitée.
const findCard = (board: Board, cardId: number): { columnId: number; card: Card } | undefined => {
  for (const column of board.columns) {
    const card = column.cards.find((c) => c.id === cardId);
    if (card) return { columnId: column.id, card };
  }
  return undefined;
};

export const applyCardCreated = (board: Board, event: CardCreatedEvent): Board => {
  if (event.board_id !== board.id) return board;
  return mapColumn(board, event.card.column_id, (cards) => {
    if (cards.some((c) => c.id === event.card.id)) return cards;
    return sortByPosition([...cards, event.card]);
  });
};

export const applyCardUpdated = (board: Board, event: CardUpdatedEvent): Board => {
  if (event.board_id !== board.id) return board;

  const located = findCard(board, event.card.id);
  if (!located) return board;

  // column_id divergent = un CardMoved concurrent a déjà replacé la carte :
  // on garde ce placement local plutôt que le column_id/position périmés.
  const patchedCard: Card =
    located.columnId === event.card.column_id
      ? event.card
      : { ...event.card, column_id: located.columnId, position: located.card.position };

  return mapColumn(board, located.columnId, (cards) =>
    sortByPosition(cards.map((c) => (c.id === patchedCard.id ? patchedCard : c)))
  );
};

export const applyCardMoved = (board: Board, event: CardMovedEvent): Board => {
  if (event.board_id !== board.id) return board;

  const located = findCard(board, event.card.id);

  // Un déplacement ne porte que column_id/position ; le reste vient de
  // l'état local pour ne pas écraser un CardUpdated concurrent reçu
  // entre-temps. Carte inconnue localement : snapshot complet de l'event.
  const movedCard: Card = located
    ? { ...located.card, column_id: event.card.column_id, position: event.card.position }
    : event.card;

  const withoutCard = mapColumn(board, event.from_column_id, (cards) =>
    cards.filter((c) => c.id !== event.card.id)
  );
  return mapColumn(withoutCard, movedCard.column_id, (cards) => {
    const withCard = cards.some((c) => c.id === movedCard.id) ? cards : [...cards, movedCard];
    return sortByPosition(withCard.map((c) => (c.id === movedCard.id ? movedCard : c)));
  });
};

export const applyCardDeleted = (board: Board, event: CardDeletedEvent): Board => {
  if (event.board_id !== board.id) return board;
  return mapColumn(board, event.column_id, (cards) => cards.filter((c) => c.id !== event.card_id));
};

// Retire localement un membre de toutes les cartes après son retrait du board.
export const removeUserFromAllCards = (board: Board, userId: number): Board => ({
  ...board,
  columns: board.columns.map((column) => {
    let changed = false;
    const cards = column.cards.map((card) => {
      if (!card.users?.some((u) => u.id === userId)) return card;
      changed = true;
      return { ...card, users: card.users.filter((u) => u.id !== userId) };
    });
    return changed ? { ...column, cards } : column;
  }),
});
